import { Camera } from 'lucide-react';
import { Button } from '@/shared/widgets/button';
import { useImageSearch, type UseImageSearchOptions } from '../hooks/useImageSearch';

export interface ImageSearchProps extends UseImageSearchOptions {
  className?: string;
}

export default function ImageSearch({
  enabled,
  onOpen,
  onSuccess,
  allowedExtensions,
  maxFileSize,
  className = '',
}: ImageSearchProps) {
  const {
    isEnabled,
    isImageUploadOpen,
    file,
    error,
    isSubmittingImage,
    fileInputRef,
    openUploadPanel,
    handleFileChange,
    handleSubmit,
    triggerFilePicker,
  } = useImageSearch({
    enabled,
    onOpen,
    onSuccess,
    allowedExtensions,
    maxFileSize,
  });

  if (!isEnabled) return null;

  return (
    <div className={className}>
      <div className="pt-4 border-t border-gray-200">
        <div className="relative overflow-hidden rounded-md">
          <div className="min-h-[84px]" />

          <div
            className={[
              'absolute inset-0 flex items-start',
              'transition-[transform,opacity] duration-300 ease-out transform-gpu',
              isImageUploadOpen
                ? '-translate-x-full opacity-0 pointer-events-none'
                : 'translate-x-0 opacity-100',
            ].join(' ')}
          >
            <button
              type="button"
              className="w-full flex items-start justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors py-2"
              onClick={openUploadPanel}
            >
              <Camera className="h-4 w-4" />
              <span>Search designs by images</span>
            </button>
          </div>

          <div
            className={[
              'absolute inset-0 flex items-start',
              'transition-[transform,opacity] duration-300 ease-out transform-gpu',
              isImageUploadOpen
                ? 'translate-x-0 opacity-100'
                : 'translate-x-full opacity-0 pointer-events-none',
            ].join(' ')}
          >
            <form className="w-full py-2" onSubmit={handleSubmit}>
              <div className="flex w-full items-stretch overflow-hidden rounded-md border border-gray-300 bg-white">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.jpg,.jpeg,.png,.heic,.heif"
                  onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                  className="hidden"
                  aria-hidden
                />
                <button
                  type="button"
                  className="flex shrink-0 items-center border-r border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 transition-colors hover:bg-gray-100"
                  onClick={triggerFilePicker}
                >
                  Choose file
                </button>
                <span className="min-w-0 flex-1 self-center truncate px-3 text-sm text-gray-600">
                  {file?.name ?? 'No file chosen'}
                </span>
                <Button
                  type="submit"
                  isLoading={isSubmittingImage}
                  disabled={!file}
                  className="h-auto min-h-10 shrink-0 self-stretch rounded-none px-6 text-xs font-bold uppercase tracking-wider bg-[#F46D5E] hover:bg-[#F46D5E]/90 text-white disabled:opacity-50"
                >
                  Search
                </Button>
              </div>
              {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
